import styled from 'styled-components';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { TooltipStyle } from './tooltip-style';
import { FormattedMoney } from '../atomic-components';
import { BankHistory } from '@algoan/dashboard/model';
import { addAlpha } from '@algoan/deep-dive/theme';

interface BalanceDifferenceTooltipProps {
  bankHistory: BankHistory;
  x: number;
  y: number;
}

export const tooltipWidth = 248;

export const BalanceDifferenceTooltip: React.FC<BalanceDifferenceTooltipProps> = ({ bankHistory, x = 0, y = 0 }) => {
  return (
    <foreignObject x={x} y={y} width={tooltipWidth} height="30" overflow="visible">
      <S.Tooltip>
        <S.Date>
          <FormattedDate value={bankHistory.date} day="numeric" month="numeric" />
        </S.Date>
        <S.Divider />
        <S.BalanceAndDifference>
          <tbody>
            <tr>
              <S.Text>
                <FormattedMessage
                  id="bank_history_chart.tooltip.balance"
                  defaultMessage="Balance"
                  description="Label of the balance row in one of the BankHistoryChart tooltip"
                />
              </S.Text>
              <S.Value isPositive={bankHistory.balance > 0}>
                <FormattedMoney number={bankHistory.balance} />
              </S.Value>
            </tr>
            <tr>
              <S.Text>
                <FormattedMessage
                  id="bank_history_chart.tooltip.evolution"
                  defaultMessage="Evolution"
                  description="Label of the evolution row in one of the BankHistoryChart tooltip"
                />
              </S.Text>
              <S.Value isPositive={bankHistory.difference > 0}>
                <FormattedMoney number={bankHistory.difference} />
              </S.Value>
            </tr>
          </tbody>
        </S.BalanceAndDifference>
      </S.Tooltip>
    </foreignObject>
  );
};

export const S = {
  Tooltip: styled(TooltipStyle.Tooltip)`
    align-items: center;
    height: 50px;
    border-radius: 5px;
    box-shadow: 0px 1px 15px 0px #00466b26;
  `,
  Date: styled(TooltipStyle.Date)`
    font-size: 12px;
    color: ${({ theme }) => theme.algoanBlue};
    font-weight: 600;
    line-height: 15px;
    margin: 9px 8px;
  `,
  Divider: styled(TooltipStyle.Divider)`
    height: 50px;
    background-color: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
  `,
  BalanceAndDifference: styled.table`
    border-collapse: collapse;
    line-height: 15px;
    margin: 8px;
    min-width: 183px;
  `,
  Text: styled.td`
    color: ${({ theme }) => theme.algoanBlue};
    text-align: left;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
  `,
  Value: styled.td<{ isPositive: boolean }>`
    color: ${({ isPositive, theme }) =>
      isPositive ? theme.scoreIndicatorColors.positiveValue : theme.scoreIndicatorColors.negativeValue};
    font-size: 13px;
    font-weight: 800;
    padding: 1px 0;
    text-align: right;
    white-space: nowrap;
  `,
};
